import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { AppLink as Link } from '@components/AppLink'
import { Icon } from './Icon';
import { LandingLogo } from './LandingLogo';
import { useStore } from '@store/StoreProvider';
import { LangSwitcher } from '@components/LangSwitcher';
import { lockBodyScroll, unlockBodyScroll } from '@utils/bodyScrollLock';
import { setSiteSupportWidgetHidden } from '@utils/siteSupportWidget';
import { SectionLink, landingSectionLabel } from './sectionNav';
import { SUPPORT_BOT_HANDLE, SUPPORT_BOT_URL, TELEGRAM_NEWS_URL, supportBotLabel } from '@constants/support';
import { siteHref } from '@utils/appDomain'

type NavItem = { label: string; section: string } | { label: string; to: string };

export const LandingHeader: React.FC = observer(() => {
  const { auth, i18n } = useStore();
  const isLoggedIn = auth.isAuthenticated;
  const [menuOpen, setMenuOpen] = useState(false);
  const headerInnerRef = useRef<HTMLDivElement>(null);
  const b1Ref = useRef<HTMLSpanElement>(null);
  const b2Ref = useRef<HTMLSpanElement>(null);
  const b3Ref = useRef<HTMLSpanElement>(null);

  void i18n.language
  void i18n.version
  const tf = (key: string) => i18n.t(key)
  const botLabel = supportBotLabel(tf, i18n.language)

  useEffect(() => {
    const onScroll = () => {
      const el = headerInnerRef.current;
      if (!el) return;
      if (window.scrollY > 20) {
        el.classList.add('border-white/10', 'shadow-lg', 'shadow-black/30');
        el.classList.remove('border-transparent');
      } else {
        el.classList.remove('border-white/10', 'shadow-lg', 'shadow-black/30');
        el.classList.add('border-transparent');
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-lock', menuOpen);
    // iOS ignores overflow:hidden on <body> — pin it instead; also hide the chat
    // bubble, which floats above the menu and covers its bottom buttons.
    if (menuOpen) lockBodyScroll();
    else unlockBodyScroll();
    setSiteSupportWidgetHidden(menuOpen);
    if (b1Ref.current) b1Ref.current.style.transform = menuOpen ? 'translateY(6px) rotate(45deg)' : '';
    if (b2Ref.current) b2Ref.current.style.opacity = menuOpen ? '0' : '1';
    if (b3Ref.current) b3Ref.current.style.transform = menuOpen ? 'translateY(-6px) rotate(-45deg)' : '';
    return () => {
      document.body.classList.remove('menu-lock');
      unlockBodyScroll();
      setSiteSupportWidgetHidden(false);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((v) => !v);

  /** Desktop nav — as in index_8. `section` → landing anchor, `to` → route. */
  const desktopLinks: NavItem[] = [
    { section: 'features', label: landingSectionLabel(tf, 'features') },
    { section: 'privacy', label: landingSectionLabel(tf, 'privacy') },
    { section: 'pricing', label: landingSectionLabel(tf, 'pricing') },
    { to: '/knowledge-base', label: tf('nav.knowledge_base') },
    { section: 'partners', label: landingSectionLabel(tf, 'partners') },
  ];

  /** Mobile nav — as in index_8 (без Приватности) */
  const mobileLinks: NavItem[] = [
    { section: 'features', label: landingSectionLabel(tf, 'features') },
    { section: 'pricing', label: landingSectionLabel(tf, 'pricing') },
    { to: '/knowledge-base', label: tf('nav.knowledge_base') },
    { section: 'partners', label: landingSectionLabel(tf, 'partners') },
  ];

  return (
    <>
      <header id="header" className="fixed top-0 inset-x-0 z-50 transition-all duration-300">
        <div
          ref={headerInnerRef}
          id="headerInner"
          className="glass border-b border-transparent transition-all duration-300"
        >
          <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 group" onClick={closeMenu}>
              <LandingLogo className="group-hover:scale-105 transition-transform" />
            </Link>

            <ul className="land-desktop-flex items-center gap-9 text-sm font-medium text-slate-300">
              {desktopLinks.map((link) => (
                <li key={link.label}>
                  {'to' in link ? (
                    <Link to={link.to} className="nav-link hover:text-white transition">
                      {link.label}
                    </Link>
                  ) : (
                    <SectionLink section={link.section} className="nav-link hover:text-white transition">
                      {link.label}
                    </SectionLink>
                  )}
                </li>
              ))}
            </ul>

            <div className="land-desktop-flex items-center gap-3">
              <LangSwitcher />
              <a
                href={SUPPORT_BOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={botLabel}
                title={botLabel}
                className="grid place-items-center w-10 h-10 rounded-xl glass border border-white/10 text-brand2 hover:text-white hover:border-brand/40 transition"
              >
                <Icon id="ic-message" className="ic ic-sm" />
              </a>
              <a
                href={TELEGRAM_NEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram-канал"
                className="grid place-items-center w-10 h-10 rounded-xl glass border border-white/10 text-brand2 hover:text-white hover:border-brand/40 transition"
              >
                <Icon id="ic-send" className="ic ic-sm" />
              </a>
              {isLoggedIn ? (
                <Link
                  to="/myProfile"
                  className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-brand hover:bg-blue-600 shadow-lg shadow-brand/30 transition active:scale-95 text-white"
                >
                  {tf('nav.to_profile')}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-2.5 text-sm font-semibold text-slate-200 hover:text-white transition"
                  >
                    {tf('app.actions.sign_in')}
                  </Link>
                  <a
                    href={siteHref('/register')}
                    className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-brand hover:bg-blue-600 shadow-lg shadow-brand/30 transition active:scale-95 text-white"
                  >
                    {tf('auth.sign_up')}
                  </a>
                </>
              )}
            </div>

            <button
              type="button"
              id="burger"
              aria-label={tf('nav.menu')}
              className="land-mobile-only grid place-items-center w-11 h-11 rounded-xl glass border border-white/10 active:scale-90 transition"
              onClick={toggleMenu}
            >
              <span className="block w-5 space-y-1.5">
                <span ref={b1Ref} className="block h-[2px] bg-white rounded transition-all" />
                <span ref={b2Ref} className="block h-[2px] bg-white rounded transition-all" />
                <span ref={b3Ref} className="block h-[2px] bg-white rounded transition-all" />
              </span>
            </button>
          </nav>
        </div>
      </header>

      <div
        id="mobileMenu"
        className={`land-mobile-menu fixed inset-0 z-[60] ${menuOpen ? 'menu-open' : 'menu-closed'}`}
      >
        <div className="absolute inset-0 bg-deep/97 backdrop-blur-xl" />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-brand/12 via-transparent to-indigo-600/12" />

        <div className="land-mobile-menu-scroll relative h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
        <div className="min-h-full w-full max-w-md mx-auto flex flex-col px-6 sm:px-7"
          style={{
            paddingTop: 'max(1.1rem, env(safe-area-inset-top))',
            paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div className="menu-item flex items-center justify-between py-1">
            <Link to="/" onClick={closeMenu}>
              <LandingLogo />
            </Link>
            <button
              type="button"
              id="menuClose"
              aria-label="Закрыть"
              className="grid place-items-center w-11 h-11 rounded-xl glass border border-white/10 text-white active:scale-90 transition"
              onClick={closeMenu}
            >
              <Icon id="ic-x" />
            </button>
          </div>

          <div className="menu-item mt-7 mb-1 text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold">
            {tf('nav.navigation')}
          </div>

          <nav className="flex flex-col">
            {mobileLinks.map((link) =>
              'to' in link ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="menu-item mob-link group flex items-center justify-between text-[1.7rem] font-extrabold py-3.5 border-b border-white/5"
                  onClick={closeMenu}
                >
                  {link.label}
                  <Icon id="ic-arrow-right" className="ic text-slate-500 group-active:text-brand2 transition" />
                </Link>
              ) : (
                <SectionLink
                  key={link.label}
                  section={link.section}
                  className="menu-item mob-link group flex items-center justify-between text-[1.7rem] font-extrabold py-3.5 border-b border-white/5"
                  onNavigate={closeMenu}
                >
                  {link.label}
                  <Icon id="ic-arrow-right" className="ic text-slate-500 group-active:text-brand2 transition" />
                </SectionLink>
              ),
            )}
          </nav>

          <div className="menu-item mt-7">
            <div className="text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold mb-2">
              Язык
            </div>
            <LangSwitcher variant="pills" />
          </div>

          <div className="menu-item mt-7">
            <div className="text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold mb-2">
              {tf('nav.footer.contact')}
            </div>
            <a
              href={SUPPORT_BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 border-b border-white/5 active:opacity-70 transition"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-message" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">{botLabel}</span>
                <span className="block text-xs text-slate-500">{SUPPORT_BOT_HANDLE}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm text-slate-500 shrink-0" />
            </a>
            <a
              href={TELEGRAM_NEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 border-b border-white/5 active:opacity-70 transition"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-send" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">
                  Telegram-канал
                </span>
                <span className="block text-xs text-slate-500">@chatx</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm text-slate-500 shrink-0" />
            </a>
            <a
              href="mailto:support@chatx.click"
              className="flex items-center gap-3 py-3 active:opacity-70 transition"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-mail" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">
                  {tf('help.contacts.email')}
                </span>
                <span className="block text-xs text-slate-500">support@chatx.click</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm text-slate-500 shrink-0" />
            </a>
          </div>

          <div className="flex-1 min-h-4" />

          <div className="menu-item flex flex-col gap-3">
            {isLoggedIn ? (
              <Link
                to="/myProfile"
                className="mob-link text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-lg shadow-lg shadow-brand/30 active:scale-95 transition text-white"
                onClick={closeMenu}
              >
                {tf('nav.to_profile')} →
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="mob-link text-center py-4 rounded-2xl glass border border-white/10 font-semibold text-lg active:scale-95 transition"
                  onClick={closeMenu}
                >
                  {tf('app.actions.sign_in')}
                </Link>
                <SectionLink
                  section="cta"
                  className="mob-link text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-lg shadow-lg shadow-brand/30 active:scale-95 transition text-white"
                  onNavigate={closeMenu}
                >
                  {tf('auth.sign_up')} →
                </SectionLink>
              </>
            )}
          </div>
          <div className="menu-item mt-4 text-center text-[11px] text-slate-500">
            Powered by <span className="text-brand2 font-semibold">ChatX</span>
            {' · '}
            {tf('billing.plans.offshore_privacy')}
          </div>
        </div>
        </div>
      </div>
    </>
  );
});
